import { CommandItem } from "@/components/shadcn/command";
import { highlightMatch } from "@/components/search/highlight-match";
import {
  comboSetToken,
  commitToken,
  isCommittableRow,
  orderComponentsByQuery,
  type FlatRow,
} from "@/components/search/suggestion-rows";

function ReportStatus({ inLibrary }: { inLibrary: boolean | undefined }) {
  if (inLibrary === true) {
    return (
      <span className="shrink-0 whitespace-nowrap text-xs font-medium text-brand">
        Raport gotowy
      </span>
    );
  }
  if (inLibrary === false) {
    return (
      <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
        Nowy raport · kilka minut
      </span>
    );
  }
  return null;
}

// One report per row. A combination row commits the whole set; its component
// chips commit one substance each.
export function SuggestionRow({
  row,
  value,
  query,
  onCommit,
}: {
  row: FlatRow;
  value: string;
  query: string;
  onCommit: (submit: string, label: string) => void;
}) {
  const { group } = row;
  const jointReportBlocked = !isCommittableRow(group);
  const components = group.is_combination
    ? orderComponentsByQuery(group.components ?? [], query)
    : [];
  const commitRow = () =>
    group.is_combination
      ? onCommit(comboSetToken(group), group.substance)
      : onCommit(commitToken(group.substance), group.substance);

  return (
    <CommandItem
      value={value}
      disabled={jointReportBlocked}
      onSelect={jointReportBlocked ? undefined : commitRow}
      data-testid="suggestion-row"
      className="flex min-h-11 items-start border-l-2 border-l-transparent px-3 py-2 data-[selected=true]:border-l-brand data-[selected=true]:bg-accent data-[disabled=true]:opacity-100"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-start justify-between gap-3">
          <span
            className={
              jointReportBlocked
                ? "line-clamp-2 text-sm font-semibold text-muted-foreground"
                : "line-clamp-2 text-sm font-semibold text-foreground"
            }
          >
            {highlightMatch(group.substance, query)}
          </span>
          {!jointReportBlocked && <ReportStatus inLibrary={group.in_library} />}
        </span>
        {group.matched_brand && (
          <span className="truncate text-sm text-muted-foreground">
            Marka: {highlightMatch(group.matched_brand, query)}
          </span>
        )}
        {jointReportBlocked && (
          <span className="text-sm text-muted-foreground">
            Za dużo składników na jeden raport — wybierz substancję
          </span>
        )}
        {components.length > 0 && (
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Osobno:</span>
            {components.map((component) => (
              <button
                key={component.substance}
                type="button"
                aria-label={`Raport tylko dla ${component.substance}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  onCommit(commitToken(component.substance), component.substance);
                }}
                // pointer-events-auto: a disabled cmdk item turns pointer
                // events off for its subtree; the chips must stay clickable.
                className="pointer-events-auto rounded-full border border-border bg-background px-2 py-0.5 text-xs text-foreground transition-colors hover:border-brand/50 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {highlightMatch(component.substance, query)}
              </button>
            ))}
          </span>
        )}
      </span>
    </CommandItem>
  );
}
